import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GripVertical, Trash2, FolderKanban, ArrowRight } from "lucide-react";

const fmtShort = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });

function Title({ it, editable, onRename }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState(it.title);
    const canRename = editable;
    const save = () => { setEditing(false); if (val.trim() && val.trim() !== it.title) onRename(val.trim()); else setVal(it.title); };
    if (editing) return (
        <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setVal(it.title); setEditing(false); } }}
            data-testid={`plan-item-title-input-${it.id}`} className="w-full text-sm border-b border-neutral-400 focus:outline-none bg-transparent" />
    );
    return (
        <span onClick={() => canRename && setEditing(true)} data-testid={`plan-item-title-${it.id}`}
            className={`text-sm ${it.completed ? "line-through text-neutral-400" : "text-neutral-900"} ${canRename ? "cursor-text hover:bg-neutral-50 rounded px-0.5 -mx-0.5" : ""}`}>
            {it.title}
        </span>
    );
}

function Origin({ it }) {
    if (it.kind === "free") return <span className="text-[10px] uppercase tracking-wider text-neutral-400" data-testid={`plan-item-free-${it.id}`}>Task libera</span>;
    if (it.missing) return <span className="text-xs text-red-600">Task non più disponibile nel progetto</span>;
    return (
        <Link to={it.link} className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 truncate" data-testid={`plan-item-origin-${it.id}`}>
            <FolderKanban size={11} className="shrink-0" />
            <span className="truncate">{[it.project_title, it.client_name, it.program_title, it.parent_title].filter(Boolean).join(" · ")}</span>
        </Link>
    );
}

export function PlanItem({ it, editable, onToggle, onRename, onDelete, onReplace, onKeep, dragProps }) {
    const moved = !!it.moved_to;
    return (
        <li {...(editable ? dragProps : {})} data-testid={`plan-item-${it.id}`}
            className={`group flex items-start gap-3 px-3 py-2.5 bg-white border rounded-md transition-colors ${it.kind === "task" ? "border-neutral-200 border-l-4 border-l-neutral-800" : "border-neutral-200 border-l-4 border-l-[#FFE500]"} ${moved ? "opacity-60" : ""}`}>
            {editable && <GripVertical size={14} className="text-neutral-300 group-hover:text-neutral-500 cursor-grab mt-1 shrink-0" data-testid={`plan-item-handle-${it.id}`} />}
            <input type="checkbox" checked={!!it.completed} disabled={!editable || it.missing || it.not_checkable} onChange={(e) => onToggle(e.target.checked)}
                data-testid={`plan-item-check-${it.id}`} className="mt-1 h-4 w-4 accent-neutral-900 shrink-0" />
            <div className="flex-1 min-w-0 space-y-0.5">
                <Title it={it} editable={editable} onRename={onRename} />
                <div className="flex items-center gap-3 min-w-0">
                    <Origin it={it} />
                    {moved && <span className="text-xs text-amber-700 inline-flex items-center gap-1 shrink-0" data-testid={`plan-item-moved-${it.id}`}><ArrowRight size={11} /> spostata a {fmtShort(it.moved_to)}</span>}
                    {it.completed && it.completed_at && !moved && <span className="text-xs text-green-700 shrink-0">completata</span>}
                </div>
                {it.not_checkable && !it.keep_parent && editable && (
                    <div className="mt-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2 py-1.5 flex flex-wrap items-center gap-2" data-testid={`plan-item-subtasks-warning-${it.id}`}>
                        Questa task ora ha {it.subtask_count} task secondarie e non si spunta più direttamente. Sostituirla nel piano con le sue secondarie?
                        <button onClick={onReplace} data-testid={`plan-item-replace-${it.id}`} className="px-2 py-0.5 rounded bg-neutral-900 text-white">Sostituisci</button>
                        <button onClick={onKeep} data-testid={`plan-item-keep-${it.id}`} className="px-2 py-0.5 rounded border border-amber-300">Lascia così</button>
                    </div>
                )}
                {it.not_checkable && (it.keep_parent || !editable) && <div className="text-[11px] text-neutral-500">Si completa automaticamente dalle sue {it.subtask_count} task secondarie</div>}
            </div>
            {editable && (
                <button onClick={onDelete} title={it.kind === "task" ? "Togli dal piano (la task resta nel progetto)" : "Elimina"}
                    data-testid={`plan-item-delete-${it.id}`} className="opacity-40 group-hover:opacity-100 text-neutral-400 hover:text-red-600 transition-opacity">
                    <Trash2 size={14} />
                </button>
            )}
        </li>
    );
}
